import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  MapPin,
  Filter,
  Download,
  Clock,
  Target,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Zap,
  Shield,
  FileText,
  Search,
  Calendar,
  Send } from
'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicAdjusters = () => {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  React.useEffect(() => {
    document.title = 'Zeus Weather for Public Adjusters - Storm Date Verification Made Simple';
    window.scrollTo(0, 0);
  }, []);

  const scrollToCTA = () => {
    document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setErrorMessage('');

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        message: `[From Public Adjusters Page] ${formData.message}`
      });

      if (response.data.success) {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setErrorMessage('Failed to send message. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
      setErrorMessage('Failed to send message. Please try emailing us directly at joe@zeusweatherllc.com');
      console.error('Contact form error:', error);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200/20 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-2">
              <Link to="/">
                <img
                  src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/dn3jwrfn_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2004_14_33%20PM.png"
                  alt="Zeus Weather"
                  className="h-10 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => window.open('https://portal.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900"
              >
                Login
              </Button>
              <Button
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white transition-all duration-300 hover:scale-105">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}></div>
        </div>

        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-blue-500/20 border border-blue-400/30 text-blue-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Built for Public Adjusters
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl !font-bold !text-4xl mb-6 text-white">
              Stop Hunting Through Storm Swaths. Get the Dates You Need Instantly.
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 mb-8 leading-relaxed">
              Zeus gives public adjusters verified storm dates, hail sizes, and wind speeds for any address —
              so you can build stronger claims faster, without the guesswork.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10 text-left">
              <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-blue-100">Look up any address, get exact storm dates</span>
              </div>
              <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-blue-100">Verified hail size, wind speed, and severity data</span>
              </div>
              <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-blue-100">Download reports to support your claims</span>
              </div>
              <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-blue-100">No more sifting through NOAA data manually</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-2xl">
                Start Your 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 transition-all duration-300">
                Request a Demo
              </Button>
            </div>

            <p className="text-sm text-blue-200 mt-6">Trusted by public adjusters across the U.S.</p>
          </div>
        </div>
      </section>

      {/* Problem → Solution Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                You Shouldn't Need 5 Tabs Open Just to Verify a Storm Date
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-2 border-red-100 bg-red-50/50">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">The Old Way</h3>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">&#10007;</span>
                      Digging through NOAA archives for storm dates
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">&#10007;</span>
                      Cross-referencing multiple weather sources
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">&#10007;</span>
                      Guessing whether a storm actually hit the property
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">&#10007;</span>
                      Wasting hours on research per claim
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">&#10007;</span>
                      Weak documentation that carriers push back on
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">With Zeus</h3>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0" />
                      Type an address, get every storm date instantly
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0" />
                      One verified source — hail size, wind speed, severity
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0" />
                      Property-level confirmation that the storm hit
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0" />
                      Research that took hours now takes seconds
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0" />
                      Downloadable reports that stand up to carrier scrutiny
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How Zeus Works for Public Adjusters */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How Zeus Works for Public Adjusters
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Three steps from address to verified storm documentation.
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-6">
            {/* Step 1 */}
            <Card className="border-2 border-slate-200 hover:border-blue-500 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <Search className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-blue-600 mb-1">Step 1</div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      Look Up Any Address
                    </h3>
                    <p className="text-slate-700 mb-3 text-base">
                      Enter the property address and Zeus instantly pulls every storm event that impacted it.
                      No digging through NOAA archives, no cross-referencing multiple weather sites.
                      Just type the address and get results.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="border-2 border-slate-200 hover:border-blue-500 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-blue-600 mb-1">Step 2</div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      Get Verified Storm Dates and Details
                    </h3>
                    <p className="text-slate-700 mb-3 text-base">
                      See the exact dates storms hit, along with hail size, wind speed, and severity data.
                      Every data point is sourced from NOAA radar, National Weather Service reports, and verified
                      damage records — the same sources carriers use.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="border-2 border-slate-200 hover:border-blue-500 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileText className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-blue-600 mb-1">Step 3</div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      Download Reports for Your Claims
                    </h3>
                    <p className="text-slate-700 mb-3 text-base">
                      Generate professional storm reports with all the data you need to support your claim.
                      Include them in your documentation package to build a stronger case that stands up
                      to carrier scrutiny.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section id="benefits" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Why Public Adjusters Choose Zeus
              </h2>
              <p className="text-xl text-slate-600">Spend less time on research. Build stronger claims.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Hours of Research in Seconds
                  </h3>
                  <p className="text-slate-600">
                    Instead of hunting through storm swaths and weather archives, Zeus gives you the dates
                    you need for any address instantly. What used to take hours now takes a few clicks.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Data Carriers Can't Dispute
                  </h3>
                  <p className="text-slate-600">
                    Zeus uses the same NOAA and NWS data sources that insurance carriers reference.
                    Your storm documentation matches their own records, making pushback harder.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Property-Level Precision
                  </h3>
                  <p className="text-slate-600">
                    Don't just know a storm hit the area — know it hit the specific property.
                    Zeus provides address-level storm verification, not just zip code or county data.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <Download className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Professional Reports, Ready to Attach
                  </h3>
                  <p className="text-slate-600">
                    Download clean, professional storm reports that you can include directly in your claims package.
                    No reformatting, no screenshots — just attach and submit.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <Zap className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Get Alerts When Storms Hit Your Territory
                  </h3>
                  <p className="text-slate-600">
                    Set up watch zones for the areas you serve. When a storm hits, Zeus notifies you
                    with impact data so you can start working claims before anyone else.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Handle More Claims, Not More Paperwork
                  </h3>
                  <p className="text-slate-600">
                    By cutting research time dramatically, you can take on more claims without
                    adding overhead. Zeus helps you scale your business, not your busywork.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Visualization Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                See Storm Impact Data at a Glance
              </h2>
              <p className="text-xl text-blue-100">
                Powerful storm verification tools designed for public adjusters
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_522c5977-b846-493f-b349-2811165f83cd/artifacts/zgokcjhw_mobile.png"
                  alt="Zeus Weather Storm Verification"
                  className="rounded-lg shadow-2xl border-4 border-white/20 max-w-[280px] w-full h-auto" />
              </div>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Storm Impact Overlay</h3>
                    <p className="text-blue-100">
                      See exactly which properties were hit, with color-coded severity indicators showing hail size and wind damage levels.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Complete Storm History</h3>
                    <p className="text-blue-100">
                      Access the full storm history for any address. See every event with dates, hail sizes, and wind speeds going back years.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">One-Click Reports</h3>
                    <p className="text-blue-100">
                      Generate professional storm verification reports instantly. Include them directly in your claims documentation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                One Platform. One Price. Unlimited Lookups.
              </h2>
              <p className="text-lg text-slate-600 mb-4">
                Pays for itself with one claim. No per-lookup fees. No hidden costs.
              </p>
              <div className="inline-block bg-green-100 text-green-800 px-6 py-3 rounded-full font-semibold text-lg mb-8">
                30-Day Free Trial
              </div>

              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-4 mb-12">
                <span className={`text-lg font-semibold ${selectedPlan === 'monthly' ? 'text-slate-900' : 'text-slate-500'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setSelectedPlan(selectedPlan === 'monthly' ? 'annual' : 'monthly')}
                  className="relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  style={{ backgroundColor: selectedPlan === 'annual' ? '#2563eb' : '#cbd5e1' }}>
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    selectedPlan === 'annual' ? 'translate-x-9' : 'translate-x-1'}`
                    } />
                </button>
                <span className={`text-lg font-semibold ${selectedPlan === 'annual' ? 'text-slate-900' : 'text-slate-500'}`}>
                  Annual
                </span>
                {selectedPlan === 'annual' &&
                <span className="ml-2 inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                    Save $300/year
                  </span>
                }
              </div>
            </div>

            {/* Single Pricing Card */}
            <Card className="border-4 border-blue-600 relative hover:shadow-xl transition-shadow duration-300 max-w-md mx-auto">
              <CardContent className="p-10">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">Zeus</h3>
                  <p className="text-base text-slate-600 mb-4">
                    Unlimited storm lookups. Pays for itself with one claim.
                  </p>
                  <div className="mb-6">
                    {selectedPlan === 'monthly' ?
                    <>
                        <p className="text-5xl font-bold text-slate-900">
                          $150
                          <span className="text-xl text-slate-600">/month</span>
                        </p>
                        <p className="text-sm text-slate-500 mt-2">Billed monthly</p>
                      </> :
                    <>
                        <p className="text-5xl font-bold text-slate-900">
                          $1,500
                          <span className="text-xl text-slate-600">/year</span>
                        </p>
                        <p className="text-sm text-slate-500 mt-2">
                          $125/month &bull; Save $300 annually
                        </p>
                      </>
                    }
                  </div>
                  <p className="text-sm text-slate-600">
                    Cancel anytime. No contracts.
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited address lookups</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited storm report downloads</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Full storm history per property</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Hail size, wind speed, and severity data</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Real-time storm alerts for your watch zones</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Mobile app for iOS and Android</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Priority support</span>
                  </li>
                </ul>

                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
                  onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}>
                  Start Your 30-Day Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Where does Zeus get its storm data?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Zeus uses NOAA radar data, National Weather Service reports, and verified damage records to create highly accurate storm impact maps. We track hail starting at 0.75" and winds at 35mph. This is the same data that insurance carriers reference, so your documentation matches their records.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How far back does the storm history go?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Zeus provides storm history going back multiple years for any address in the United States. You can see every hail event, wind event, and severe weather occurrence that impacted a specific property, including exact dates, hail sizes, and wind speeds.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Can I use Zeus reports as evidence in claims?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. Zeus generates professional storm verification reports that include sourced data from NOAA and NWS. Public adjusters include these reports in their claims documentation to provide verified storm dates, hail sizes, and wind speeds for the specific property address.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Is there a limit on how many addresses I can look up?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  <strong>No limits.</strong> Your Zeus subscription includes unlimited address lookups and unlimited storm report downloads. Whether you're running 10 claims or 100, the price stays the same.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How is Zeus different from other weather tools?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Most weather tools are built for storm chasers or roofing contractors and require you to interpret raw storm swaths yourself. Zeus is designed for ease of use — type an address and get the verified storm dates you need. No hunting through swath maps, no manual cross-referencing. Just the dates and data you need to support your claims.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Do I need to be technical to use Zeus?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Not at all. Zeus is built for simplicity. If you can type an address into a search bar, you can use Zeus. There's no training required, no complicated setup. You'll be pulling storm reports within minutes of signing up.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact / Demo Request Section */}
      <section id="contact-section" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Want to See Zeus in Action?
              </h2>
              <p className="text-xl text-slate-600">
                Request a demo or ask us anything. We'll get back to you within 24 hours.
              </p>
            </div>

            <Card className="border-2 border-slate-200 shadow-lg">
              <CardContent className="p-8">
                {formStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-slate-600 mb-6">
                      Thank you for your interest. We'll get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setFormStatus('idle')}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-900 font-semibold mb-2">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-slate-900 font-semibold mb-2">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-slate-900 font-semibold mb-2">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your firm name"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-900 font-semibold mb-2">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your needs or request a demo..."
                        rows={6}
                        className="mt-2"
                      />
                    </div>

                    {formStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {errorMessage}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                    >
                      {formStatus === 'sending' ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <Send className="mr-2 h-5 w-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta-section" className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Stop Wasting Hours on Storm Research?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join public adjusters across the country who use Zeus to verify storm dates
              in seconds, not hours. Start your free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-2xl">
                Start Your 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 transition-all duration-300">
                Request a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 border-t border-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_522c5977-b846-493f-b349-2811165f83cd/artifacts/ch6dtlgq_logo.png"
                alt="Zeus Weather"
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400">
                Storm verification and weather data for public adjusters
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#benefits" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#contact-section" className="hover:text-white transition-colors">Request Demo</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors">For Roofing Contractors</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} Zeus Weather. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicAdjusters;
